import { Map as ImmutableMap } from 'immutable';

import {
    CANVAS_SCALE,
    CANVAS_SUB_SECTION_WIDTH,
    NOTE_SIZE,
    SECTION_HEIGHT,
    CANVAS_SECTION_MARGIN,
    SLIDE_HEIGHT,
    SLIDE_SECTION_HEIGHT,
    SLIDE_WIDTH,
    STORYBOARD_SUB_SECTION_WIDTH,
    SUB_SECTION_HEIGHT,
    SUB_SECTION_MARGIN, STORYBOARD_SECTION_MARGIN
} from "../constants";

import { createLinePath, createLineRandom, createRectPath, createRectRandom } from "./path-data";
import {
    getTemplate,
    HelpData,
    LabelData,
    MidPosition,
    Position,
    SectionData,
    SubSectionData,
    SubSectionPosition,
    Template,
    TextFieldData
} from "./template-store";
import { Locale } from "./translation-store";
import { Note } from "../reducers";


export interface Hotspot {
    readonly xMin: number,
    readonly yMin: number,
    readonly xMax: number,
    readonly yMax: number,
}

export interface LabelUIData extends LabelData {
    readonly storyboardPosition: Position | MidPosition,
}

export interface TextFieldUIData extends TextFieldData {
    readonly storyboardPosition: Position,
}

export interface SubSection extends SubSectionData {
    readonly storyboardPosition: SubSectionPosition,
    readonly canvasPath: string;
    readonly storyboardPath: string;
    readonly hotspot: Hotspot,
    readonly labels?: ReadonlyArray<LabelUIData>,
    readonly help: HelpUIData,
    readonly hovered: boolean,
}

export interface HelpUIData extends HelpData {
    readonly button: LabelUIData,
}

interface CanvasSectionUIData {
    readonly minX: number,
    readonly y: number,
    readonly width: number,
    readonly path: string,
    readonly hotspot: Hotspot,
}

interface StoryboardSectionUIData {
    readonly minX: number,
    readonly yTop: number,
    readonly ySeparator: number,
    readonly yBottom: number,
    readonly width: number,
    readonly borderPath: string,
    readonly separatorPath: string,
    readonly hotspotSlides: Hotspot,
}

export interface Section extends SectionData {
    readonly labels: ReadonlyArray<LabelUIData>,
    readonly textFields: ReadonlyArray<TextFieldUIData>,
    readonly subSections: ReadonlyArray<SubSection>,
    readonly help: HelpUIData,
    readonly canvasData: CanvasSectionUIData;
    readonly storyboardData: StoryboardSectionUIData;
    readonly noteHovered: boolean,
    readonly slideHovered: boolean,
}

export interface TemplateData {
    readonly canvasWidth: number,
    readonly canvasHeight: number,
    readonly storyboardWidth: number,
    readonly storyboardHeight: number,
    readonly sectionsWidth: number,
    readonly sections: ReadonlyArray<Section>,
    readonly texts: ImmutableMap<Locale, Map<string, string>>,
}


export function toStoryboardX(template: TemplateData, x: number): number {
    const canvasMargin = (template.canvasWidth - template.sectionsWidth) / 2;
    const storyboardMargin = (template.storyboardWidth - template.sectionsWidth * CANVAS_SCALE) / 2;
    if (x < canvasMargin - NOTE_SIZE / 2) {
        return storyboardMargin * (x + NOTE_SIZE / 2) / canvasMargin - NOTE_SIZE / 2;
    } else if (x <= canvasMargin + template.sectionsWidth - NOTE_SIZE / 2) {
        return (storyboardMargin - NOTE_SIZE / 2) + (x - (canvasMargin - NOTE_SIZE / 2)) * CANVAS_SCALE;
    } else {
        const dx = (x - (template.sectionsWidth + canvasMargin - NOTE_SIZE / 2)) * storyboardMargin / canvasMargin;
        return storyboardMargin - NOTE_SIZE / 2 + template.sectionsWidth * CANVAS_SCALE + dx;
    }
}

export function toCanvasX(template: TemplateData, x: number): number {
    const canvasMargin = (template.canvasWidth - template.sectionsWidth) / 2;
    const storyboardMargin = (template.storyboardWidth - template.sectionsWidth * CANVAS_SCALE) / 2;
    if (x < storyboardMargin - NOTE_SIZE / 2) {
        return canvasMargin * (x + NOTE_SIZE / 2) / storyboardMargin - NOTE_SIZE / 2;
    } else if (x <= storyboardMargin + template.sectionsWidth * CANVAS_SCALE - NOTE_SIZE / 2) {
        return (canvasMargin - NOTE_SIZE / 2) + (x - (storyboardMargin - NOTE_SIZE / 2)) / CANVAS_SCALE;
    } else {
        const dx = (x - (template.sectionsWidth * CANVAS_SCALE + storyboardMargin - NOTE_SIZE / 2)) * canvasMargin / storyboardMargin;
        return canvasMargin - NOTE_SIZE / 2 + template.sectionsWidth + dx;
    }
}

export function toStoryboardY(template: TemplateData, y: number): number {
    for (const section of template.sections) {
        const {yMin, yMax} = section.canvasData.hotspot;
        if (yMin <= y && y <= yMax) {
            return section.storyboardData.ySeparator - NOTE_SIZE / 2 + y - yMin;
        }
        if (yMin - CANVAS_SECTION_MARGIN < y && y < yMin) {
            return section.storyboardData.ySeparator - NOTE_SIZE / 2 - (yMin - y) * (SLIDE_SECTION_HEIGHT + STORYBOARD_SECTION_MARGIN) / CANVAS_SECTION_MARGIN ;
        }
    }
    const topSection = template.sections[0];
    const topCanvas = topSection.canvasData.hotspot.yMin;
    const topStoryboard = topSection.storyboardData.yTop;
    if (y <= topCanvas - CANVAS_SECTION_MARGIN) {
        return topStoryboard * (y + NOTE_SIZE / 2) / (topCanvas + NOTE_SIZE / 2 - CANVAS_SECTION_MARGIN) - NOTE_SIZE / 2;
    }
    if (y <= topCanvas) {
        return topStoryboard + (y - (topCanvas - CANVAS_SECTION_MARGIN)) / CANVAS_SECTION_MARGIN * SLIDE_SECTION_HEIGHT - NOTE_SIZE / 2;
    }
    const bottomSection = template.sections[template.sections.length - 1];
    const bottomCanvas = bottomSection.canvasData.hotspot.yMax;
    const bottomStoryboard = bottomSection.storyboardData.yBottom;
    return bottomStoryboard - NOTE_SIZE / 2 + (template.storyboardHeight - bottomStoryboard) * (y - bottomCanvas) / (template.canvasHeight - bottomCanvas - NOTE_SIZE / 2);
}

export function toCanvasY(template: TemplateData, y: number): number {
    for (const section of template.sections) {
        const {yTop, ySeparator, yBottom} = section.storyboardData;
        if (ySeparator - NOTE_SIZE / 2 <= y && y <= yBottom - NOTE_SIZE / 2) {
            return section.canvasData.hotspot.yMin + y - (ySeparator - NOTE_SIZE / 2);
        }
        if (yTop - NOTE_SIZE / 2 - STORYBOARD_SECTION_MARGIN < y && y < ySeparator - NOTE_SIZE / 2) {
            return section.canvasData.hotspot.yMin - (ySeparator - NOTE_SIZE / 2 - y) / (SLIDE_SECTION_HEIGHT + STORYBOARD_SECTION_MARGIN) * CANVAS_SECTION_MARGIN;
        }
    }
    const top = template.sections[0].storyboardData.yTop;
    if (y <= top) {
        return (template.sections[0].canvasData.hotspot.yMin - CANVAS_SECTION_MARGIN + NOTE_SIZE / 2) * (y + NOTE_SIZE / 2) / top - NOTE_SIZE / 2;
    }
    const bottomSection = template.sections[template.sections.length - 1];
    const bottomCanvas = bottomSection.canvasData.hotspot.yMax;
    const bottomStoryboard = bottomSection.storyboardData.yBottom;
    return bottomCanvas + (template.canvasHeight - bottomCanvas - NOTE_SIZE / 2) * (y - (bottomStoryboard - NOTE_SIZE / 2)) / (template.storyboardHeight - bottomStoryboard);
}


export function findSection(template: TemplateData, note: Note): Section | null {
    for (const section of template.sections) {
        if (doesHover(note.x, note.y, section.canvasData.hotspot)) {
            return section;
        }
        for (const subsection of section.subSections) {
            if (doesHover(note.x, note.y, subsection.hotspot)) {
                return section
            }
        }
    }
    return null;
}

export function doesHover(x: number, y: number, hotspot: Hotspot) {
    const {xMin, yMin, xMax, yMax} = hotspot;
    return xMin <= x && x <= xMax && yMin <= y && y <= yMax;
}


export function calculateInitialTemplateData(templateId: number): TemplateData {
    const template: Template = getTemplate(templateId);
    const n = template.sections.length;
    const canvasTopMargin = (template.canvasHeight - n * SECTION_HEIGHT - (n - 1) * CANVAS_SECTION_MARGIN) / 2;
    let canvasY = canvasTopMargin - CANVAS_SECTION_MARGIN - SECTION_HEIGHT;
    const storyboardTopMargin = (template.storyboardHeight - n * (SLIDE_SECTION_HEIGHT + SECTION_HEIGHT) - (n - 1) * STORYBOARD_SECTION_MARGIN) / 2;
    let storyboardY = storyboardTopMargin - STORYBOARD_SECTION_MARGIN - SLIDE_SECTION_HEIGHT - SECTION_HEIGHT;
    const sections: ReadonlyArray<Section> =
        template.sections.map(
            section => {
                canvasY += CANVAS_SECTION_MARGIN + SECTION_HEIGHT;
                storyboardY += STORYBOARD_SECTION_MARGIN + SLIDE_SECTION_HEIGHT + SECTION_HEIGHT;
                const canvasMinX = (template.canvasWidth - section.minWidth) / 2;
                const storyboardMinX = (template.storyboardWidth - section.minWidth * CANVAS_SCALE) / 2;
                const canvasMaxX = (template.canvasWidth + section.minWidth) / 2;
                const storyboardMaxX = (template.storyboardWidth + section.minWidth * CANVAS_SCALE) / 2;
                const subSections = prepareSubSections(section, canvasMinX, canvasMaxX, canvasY);
                const canvasHotspotLeft = subSections.reduce(
                    (left, subSection) => subSection.canvasPosition.anchor === 'LEFT' ? Math.max(left, subSection.hotspot.xMax) : left,
                    canvasMinX - NOTE_SIZE / 2
                );
                const canvasHotspotRight = subSections.reduce(
                    (right, subSection) => subSection.canvasPosition.anchor === 'RIGHT' ? Math.min(right, subSection.hotspot.xMin) : right,
                    canvasMaxX - NOTE_SIZE / 2
                );
                return {
                    ...section,
                    labels: section.labels.map(label => prepareLabel(label)),
                    textFields: section.textFields.map(textField => prepareTextField(textField)),
                    subSections,
                    help: { ...section.help, button: prepareLabel(section.help.button) },
                    canvasData: {
                        minX: canvasMinX,
                        y: canvasY,
                        width: section.minWidth,
                        path: createRectPath(0, 0, section.minWidth, SECTION_HEIGHT, createRectRandom("CLOSED")),
                        hotspot: {
                            xMin: canvasHotspotLeft,
                            yMin: canvasY - NOTE_SIZE / 2,
                            xMax: canvasHotspotRight,
                            yMax: canvasY + SECTION_HEIGHT - NOTE_SIZE / 2,
                        },
                    },
                    storyboardData: {
                        minX: storyboardMinX,
                        yTop: storyboardY,
                        ySeparator: storyboardY + SLIDE_SECTION_HEIGHT,
                        yBottom: storyboardY + SLIDE_SECTION_HEIGHT + SECTION_HEIGHT,
                        width: section.minWidth * CANVAS_SCALE,
                        borderPath: createRectPath(0, 0, section.minWidth * CANVAS_SCALE, SLIDE_SECTION_HEIGHT + SECTION_HEIGHT, createRectRandom("CLOSED")),
                        separatorPath: createLinePath(0, SLIDE_SECTION_HEIGHT, section.minWidth * CANVAS_SCALE, SLIDE_SECTION_HEIGHT, createLineRandom(), false),
                        hotspotSlides: {
                            xMin: storyboardMinX - SLIDE_WIDTH / 2,
                            yMin: storyboardY - SLIDE_HEIGHT / 2,
                            xMax: storyboardMaxX - SLIDE_WIDTH / 2,
                            yMax: storyboardY + SLIDE_SECTION_HEIGHT - SLIDE_HEIGHT / 2,
                        },
                    },
                    noteHovered: false,
                    slideHovered: false,
                }
            }
        );
    return {
        canvasWidth: template.canvasWidth,
        canvasHeight: template.canvasHeight,
        storyboardHeight: template.storyboardHeight,
        storyboardWidth: template.storyboardWidth,
        sectionsWidth: sections.reduce((width, section) => Math.max(width, section.canvasData.width), 0),
        sections,
        texts: template.texts,
    };
}

function calculateY(y: number): number {
    return y;
}

function prepareLabel(label: LabelData): LabelUIData {
    return {
        ...label,
        storyboardPosition: label.canvasPosition.anchor === 'MIDDLE' ?
            {
                ...label.canvasPosition,
                dy: calculateY(label.canvasPosition.dy),
            }
            :
            {
                ...label.canvasPosition,
                dx: label.canvasPosition.dx * CANVAS_SCALE,
                dy: calculateY(label.canvasPosition.dy),
            }
    }
}

function prepareTextField(textField: TextFieldData): TextFieldUIData {
    return {
        ...textField,
        storyboardPosition: {
            ...textField.canvasPosition,
            dx: textField.canvasPosition.dx * CANVAS_SCALE,
            dy: calculateY(textField.canvasPosition.dy),
        },
    }
}

function prepareSubSections(section: SectionData, minX: number, maxX: number, y: number): ReadonlyArray<SubSection> {
    const noteFieldY = y + SUB_SECTION_MARGIN;
    return section.subSections.map(
        subSection => {
            const noteFieldX = subSection.canvasPosition.anchor === 'LEFT' ? minX + subSection.canvasPosition.dx : maxX - subSection.canvasPosition.dx;
            return {
                ...subSection,
                storyboardPosition: {
                    ...subSection.canvasPosition,
                    dx: subSection.canvasPosition.dx * CANVAS_SCALE,
                },
                canvasPath: createRectPath(
                    0, 0,
                    CANVAS_SUB_SECTION_WIDTH, SUB_SECTION_HEIGHT,
                    createRectRandom("CLOSED"),
                ),
                storyboardPath: createRectPath(
                    0, 0,
                    STORYBOARD_SUB_SECTION_WIDTH, SUB_SECTION_HEIGHT,
                    createRectRandom("CLOSED"),
                ),
                hotspot: {
                    xMin: noteFieldX - NOTE_SIZE / 2,
                    yMin: noteFieldY - NOTE_SIZE / 2,
                    xMax: noteFieldX + NOTE_SIZE / 2,
                    yMax: noteFieldY + NOTE_SIZE / 2,
                },
                labels: subSection.labels ? subSection.labels.map(label => prepareLabel(label)) : undefined,
                help: { ...subSection.help, button: prepareLabel(subSection.help.button) },
                hovered: false,
            }
        }
    )
}



// WEBPACK FOOTER //
// ./src/services/template-service.ts